import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiCheckCircle,
  FiArrowRight,
  FiUsers,
  FiClock,
  FiTarget,
  FiBook,
  FiTrendingUp,
  FiAward,
  FiPhone,
  FiSettings
} = FiIcons;

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const [selectedPackage, setSelectedPackage] = useState(0);

  const services = {
    formation: {
      title: 'Formation Professionnelle',
      subtitle: 'Développement des Compétences et Excellence Opérationnelle',
      icon: FiBook,
      hero: 'https://images.unsplash.com/photo-1505413687799-90481dfc0203?auto=format&fit=crop&q=80&w=1200',
      overview: 'Nos programmes de formation professionnelle sont conçus pour développer les compétences de vos collaborateurs selon les normes ISO et les meilleures pratiques internationales. Chaque formation est adaptée au contexte spécifique de votre organisation.',
      objectives: [
        'Maîtriser les normes ISO applicables à votre secteur',
        'Développer les compétences managériales et techniques',
        'Favoriser l\'amélioration continue',
        'Renforcer la culture qualité dans l\'organisation',
      ],
      methodology: [
        { step: '1. Diagnostic Initial', description: 'Évaluation des besoins de formation et des compétences actuelles' },
        { step: '2. Conception Pédagogique', description: 'Développement d\'un programme adapté à votre contexte' },
        { step: '3. Déploiement', description: 'Formations interactives avec études de cas pratiques' },
        { step: '4. Suivi et Évaluation', description: 'Évaluation des acquis et accompagnement post-formation' },
      ],
      packages: [
        {
          name: 'Formation ISO Fondamentale',
          duration: '2-3 jours',
          participants: '10-20',
          price: 'Sur devis',
          includes: [
            'Compréhension des exigences ISO',
            'Rôles et responsabilités',
            'Documentation et enregistrements',
            'Audit interne',
            'Certificat de participation',
          ],
        },
        {
          name: 'Formation ISO Avancée',
          duration: '4-5 jours',
          participants: '8-15',
          price: 'Sur devis',
          includes: [
            'Tous les contenus fondamentaux',
            'Cas d\'études approfondis',
            'Préparation à la certification',
            'Simulation d\'audit',
            'Support post-formation (3 mois)',
          ],
        },
        {
          name: 'Formation Intra-Entreprise Personnalisée',
          duration: 'Sur mesure',
          participants: 'Équipe complète',
          price: 'Sur devis',
          includes: [
            'Diagnostic spécifique',
            'Programme 100% personnalisé',
            'Plusieurs modules',
            'Coaching individualisé',
            'Accompagnement 6 mois',
          ],
        },
      ],
      standards: [
        'ISO 9001:2015 - Management de la Qualité',
        'ISO 14001:2015 - Management Environnemental',
        'ISO 45001:2018 - Santé et Sécurité au Travail',
        'ISO 27001:2022 - Sécurité de l\'Information',
        'ISO 50001:2018 - Efficacité Énergétique',
      ],
      benefits: [
        { icon: FiTrendingUp, title: 'Amélioration Mesurable', description: 'Augmentation de la productivité et réduction des incidents' },
        { icon: FiAward, title: 'Certification Reconnue', description: 'Attestation de formation certifiée par des organismes accrédités' },
        { icon: FiUsers, title: 'Engagement des Équipes', description: 'Renforcement de la cohésion et de l\'appropriation des processus' },
      ],
    },
    audit: {
      title: 'Audit et Évaluation',
      subtitle: 'Diagnostic Professionnel et Conformité Certifiée',
      icon: FiCheckCircle,
      hero: 'https://images.unsplash.com/photo-1505413687799-90481dfc0203?auto=format&fit=crop&q=80&w=1200',
      overview: 'Nos auditeurs IRCA/CQI réalisent des audits de conformité, de performance et d\'efficacité selon les normes ISO. Chaque audit fournit un diagnostic objectif et des recommandations concrètes pour améliorer vos systèmes de management.',
      objectives: [
        'Évaluer la conformité à la norme applicable',
        'Identifier les points forts et les axes d\'amélioration',
        'Vérifier l\'efficacité des systèmes de management',
        'Préparer à l\'audit de certification',
      ],
      methodology: [
        { step: '1. Planification', description: 'Définition du périmètre, de l\'équipe et du calendrier d\'audit' },
        { step: '2. Audit Préalable', description: 'Évaluation de la documentation et des processus clés' },
        { step: '3. Audit Principal', description: 'Visite sur site et vérification des éléments critiques' },
        { step: '4. Rapport et Recommandations', description: 'Rapport détaillé avec plan d\'action pour les non-conformités' },
      ],
      packages: [
        {
          name: 'Audit de Diagnostic',
          duration: '1-2 jours',
          participants: 'Équipe d\'audit 2-3 personnes',
          price: 'Sur devis',
          includes: [
            'Évaluation générale du système',
            'Rapport préliminaire',
            'Recommandations prioritaires',
            'Débriefing avec management',
          ],
        },
        {
          name: 'Audit de Conformité Complet',
          duration: '3-5 jours',
          participants: 'Équipe d\'audit 2-3 personnes',
          price: 'Sur devis',
          includes: [
            'Audit détaillé de tous les éléments',
            'Vérification documentaire complète',
            'Audit sur site exhaustif',
            'Rapport complet avec preuves',
            'Plan d\'action détaillé',
          ],
        },
        {
          name: 'Audit de Suivi et Surveillance',
          duration: '1-2 jours',
          participants: 'Équipe d\'audit 1-2 personnes',
          price: 'Sur devis',
          includes: [
            'Vérification du plan d\'action précédent',
            'Audit partiel des zones critiques',
            'Rapport de suivi',
            'Recommandations pour amélioration continue',
          ],
        },
      ],
      standards: [
        'ISO 9001:2015 - Management de la Qualité',
        'ISO 14001:2015 - Management Environnemental',
        'ISO 45001:2018 - Santé et Sécurité au Travail',
        'ISO 19011:2018 - Lignes directrices pour l\'audit',
      ],
      benefits: [
        { icon: FiTarget, title: 'Diagnostic Objectif', description: 'Évaluation indépendante par des auditeurs certifiés IRCA' },
        { icon: FiTrendingUp, title: 'Plan d\'Action Clair', description: 'Recommandations priorisées et réalistes pour amélioration' },
        { icon: FiAward, title: 'Préparation à la Certification', description: 'Évaluation de la maturité avant audit de certification' },
      ],
    },
    conseil: {
      title: 'Conseil Stratégique',
      subtitle: 'Accompagnement vers l\'Excellence Opérationnelle',
      icon: FiTrendingUp,
      hero: 'https://images.unsplash.com/photo-1505413687799-90481dfc0203?auto=format&fit=crop&q=80&w=1200',
      overview: 'Nous accompagnons votre organisation dans la définition et la mise en œuvre de stratégies d\'amélioration, l\'optimisation des processus et la gestion du changement. Notre approche combine expertise technique et pragmatisme contextuel.',
      objectives: [
        'Définir une stratégie d\'amélioration cohérente',
        'Structurer les systèmes de management',
        'Optimiser les processus critiques',
        'Assurer la durabilité des changements',
      ],
      methodology: [
        { step: '1. Diagnostic Stratégique', description: 'Analyse approfondie de la situation actuelle et des enjeux' },
        { step: '2. Définition de la Feuille de Route', description: 'Co-construction d\'une stratégie d\'amélioration réaliste' },
        { step: '3. Mise en Œuvre Accompagnée', description: 'Déploiement progressif avec support et coaching régulier' },
        { step: '4. Ancrage et Pérennisation', description: 'Garantie de la durabilité des changements et amélioration continue' },
      ],
      packages: [
        {
          name: 'Audit Stratégique et Recommandations',
          duration: '4-6 semaines',
          participants: 'Équipe projet 1-2 consultants',
          price: 'Sur devis',
          includes: [
            'Diagnostic approfondi',
            'Analyse des processus critiques',
            'Rapport stratégique détaillé',
            'Feuille de route pour 12-18 mois',
            'Présentation au management',
          ],
        },
        {
          name: 'Accompagnement à la Mise en Œuvre',
          duration: '6-12 mois',
          participants: 'Équipe projet 1-2 consultants',
          price: 'Sur devis',
          includes: [
            'Diagnostic initial',
            'Coaching d\'équipe projet',
            'Support à la structuration des processus',
            'Facilitation des réunions clés',
            'Rapports de suivi mensuels',
            'Ajustements en cours de route',
          ],
        },
        {
          name: 'Accompagnement Complet 18 Mois',
          duration: '18 mois',
          participants: 'Équipe projet 1-2 consultants',
          price: 'Sur devis',
          includes: [
            'Tous les éléments précédents',
            'Mentorat des managers clés',
            'Formation des auditeurs internes',
            'Audit de suivi à 6 et 12 mois',
            'Garantie de durabilité',
          ],
        },
      ],
      standards: [
        'ISO 9001:2015 - Approche processus',
        'ISO 14001:2015 - Gestion environnementale',
        'ISO 45001:2018 - Système intégré HSE',
        'Gestion du changement - Meilleures pratiques',
      ],
      benefits: [
        { icon: FiTarget, title: 'Stratégie Adaptée', description: 'Feuille de route réaliste et adaptée à votre contexte' },
        { icon: FiUsers, title: 'Appropriation Garantie', description: 'Implication des collaborateurs dès la conception' },
        { icon: FiTrendingUp, title: 'Résultats Durables', description: 'Changements ancrés et amélioration continue pérenne' },
      ],
    },
    connexes: {
      title: 'Services Connexes',
      subtitle: 'Assistance Spécialisée et Support Complémentaire',
      icon: FiSettings,
      hero: 'https://images.unsplash.com/photo-1505413687799-90481dfc0203?auto=format&fit=crop&q=80&w=1200',
      overview: 'Nous proposons des services complémentaires pour soutenir votre parcours vers la certification : assistance à la documentation, facilitation du changement, mentorat des managers et assistance directe à la certification.',
      objectives: [
        'Assurer la qualité de la documentation',
        'Faciliter l\'adhésion au changement',
        'Développer les compétences managériales',
        'Préparer à l\'audit de certification',
      ],
      methodology: [
        { step: '1. Évaluation des Besoins', description: 'Identification des services les plus pertinents' },
        { step: '2. Plan de Service', description: 'Définition du calendrier et des livrables' },
        { step: '3. Déploiement', description: 'Exécution des services avec suivi régulier' },
        { step: '4. Évaluation et Ajustements', description: 'Mesure de l\'impact et optimisation continue' },
      ],
      packages: [
        {
          name: 'Assistance à la Documentation',
          duration: '4-8 semaines',
          participants: 'Équipe documentation 1-2 consultants',
          price: 'Sur devis',
          includes: [
            'Revue de la documentation existante',
            'Structuration du système documentaire',
            'Rédaction des documents clés',
            'Formation des responsables documentation',
            'Mise en place des processus',
          ],
        },
        {
          name: 'Facilitation du Changement Organisationnel',
          duration: '3-6 mois',
          participants: 'Facilitateur 1 consultant',
          price: 'Sur devis',
          includes: [
            'Diagnostic de la culture d\'organisation',
            'Plan de communication détaillé',
            'Ateliers de co-construction',
            'Gestion des résistances',
            'Suivi de l\'appropriation',
          ],
        },
        {
          name: 'Mentorat des Managers',
          duration: '6-12 mois',
          participants: 'Mentor 1 consultant',
          price: 'Sur devis',
          includes: [
            'Coaching individuel régulier',
            'Développement du leadership',
            'Gestion du changement managérial',
            'Préparation aux audits',
            'Renforcement de l\'équipe',
          ],
        },
      ],
      standards: [
        'Normes ISO applicables',
        'Meilleures pratiques de gestion du changement',
        'Leadership et management modernes',
      ],
      benefits: [
        { icon: FiBook, title: 'Documentation Complète', description: 'Système documentaire structuré et conforme aux normes' },
        { icon: FiUsers, title: 'Équipes Engagées', description: 'Collaborateurs impliqués et motivés par le changement' },
        { icon: FiAward, title: 'Managers Compétents', description: 'Leadership renforcé et capacité à piloter les changements' },
      ],
    },
  };

  const currentService = services[serviceId] || services.formation;
  const CurrentIcon = currentService.icon;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-16 lg:pt-20"
    >
      {/* Hero Section */}
      <section className="relative h-96 overflow-hidden">
        <img
          src={currentService.hero}
          alt={currentService.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-md">
                  <SafeIcon icon={CurrentIcon} className="w-8 h-8 text-white" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                {currentService.title}
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 max-w-3xl">
                {currentService.subtitle}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6">
              Vue d'Ensemble
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
              {currentService.overview}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                  Objectifs
                </h3>
                <ul className="space-y-3">
                  {currentService.objectives.map((obj, idx) => (
                    <motion.li
                      key={idx}
                      variants={itemVariants}
                      className="flex items-start space-x-3"
                    >
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 dark:text-gray-300">
                        {obj}
                      </span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                  Bénéfices
                </h3>
                <div className="space-y-4">
                  {currentService.benefits.map((benefit, idx) => (
                    <motion.div
                      key={idx}
                      variants={itemVariants}
                      className="flex items-start space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
                    >
                      <SafeIcon icon={benefit.icon} className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-gray-800 dark:text-white">
                          {benefit.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {benefit.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">
              Notre Méthodologie
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Une approche structurée et progressive pour assurer le succès
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {currentService.methodology.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative"
              >
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg h-full">
                  <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {idx + 1}
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3 pt-2">
                    {step.step}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {step.description}
                  </p>
                </div>
                {idx < currentService.methodology.length - 1 && (
                  <div className="hidden lg:block absolute -right-3 top-1/2 transform -translate-y-1/2">
                    <SafeIcon icon={FiArrowRight} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">
              Nos Offres
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Packages adaptés à vos besoins et budgets
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {currentService.packages.map((pkg, idx) => (
              <motion.button
                key={idx}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedPackage(idx)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  selectedPackage === idx
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {pkg.name}
              </motion.button>
            ))}
          </div>

          <motion.div
            key={selectedPackage}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 lg:p-12 shadow-lg"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
                  {currentService.packages[selectedPackage].name}
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Durée
                    </p>
                    <p className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                      <SafeIcon icon={FiClock} className="mr-2 w-5 h-5" />
                      {currentService.packages[selectedPackage].duration}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Participants
                    </p>
                    <p className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                      <SafeIcon icon={FiUsers} className="mr-2 w-5 h-5" />
                      {currentService.packages[selectedPackage].participants}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Prix
                    </p>
                    <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                      {currentService.packages[selectedPackage].price}
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <h4 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
                  Inclus dans cette offre
                </h4>
                <ul className="space-y-3">
                  {currentService.packages[selectedPackage].includes.map(
                    (item, idx) => (
                      <li
                        key={idx}
                        className="flex items-start space-x-3"
                      >
                        <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 dark:text-gray-300">
                          {item}
                        </span>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>

            <motion.a
              href="tel:+237699187929"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-700 to-cyan-600 text-white font-semibold rounded-lg hover:from-blue-800 hover:to-cyan-700 transition-all"
            >
              <SafeIcon icon={FiPhone} className="mr-2 w-5 h-5" />
              Demander un Devis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Standards */}
      <section className="py-20 bg-gradient-to-br from-blue-700 to-blue-900 dark:from-blue-900 dark:to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Normes et Référentiels
            </h2>
            <p className="text-xl text-blue-100">
              Expertise dans les standards internationaux
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {currentService.standards.map((standard, idx) => (
              <motion.div
                key={idx}
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center space-x-4 p-4 bg-white/10 rounded-lg backdrop-blur-md"
              >
                <SafeIcon icon={FiAward} className="w-6 h-6 text-blue-300 flex-shrink-0" />
                <span className="text-white font-semibold">{standard}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-6">
              Prêt à Démarrer ?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Contactez-nous pour discuter de votre projet et recevoir une proposition personnalisée.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="tel:+237699187929"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-700 to-cyan-600 text-white font-semibold rounded-lg hover:from-blue-800 hover:to-cyan-700 transition-all"
              >
                <SafeIcon icon={FiPhone} className="mr-2 w-5 h-5" />
                Nous Appeler
              </motion.a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-blue-700 text-blue-700 dark:border-blue-400 dark:text-blue-400 font-semibold rounded-lg hover:bg-blue-700 hover:text-white dark:hover:bg-blue-400 dark:hover:text-gray-900 transition-all"
              >
                <SafeIcon icon={FiArrowRight} className="mr-2 w-5 h-5" />
                Nous Contacter
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default ServiceDetail;